import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import PaySession from '../../models/PaySession';
import { PaySessionUpdate } from '../../models/PaySessionUpdate';
import { deepClone } from '../../utils/utils';
import { PaySessionStatus } from '../../models/PaySessionStatus';
import { CompressedStorageService } from '../compression/compressed-storage.service';

const KEY_PAYSESSION = 'paySession';

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService {
	private compressedStorageService = inject(CompressedStorageService);
	private _paySessionSubject = new BehaviorSubject<PaySession | null>(this.compressedStorageService.getItem<PaySession>(KEY_PAYSESSION));
	paySessionObservable = this._paySessionSubject.asObservable();
	onTokenChange = this._paySessionSubject.pipe(
		map(p => p?.sessionToken ?? null),
		distinctUntilChanged()
	);

	constructor() {
		this._paySessionSubject.subscribe(paySession => {
			this.compressedStorageService.setItem(KEY_PAYSESSION, paySession);
		});
	}

	get paySession(): PaySession | null {
		return this._paySessionSubject.value;
	}

	get token(): string | null {
		return this._paySessionSubject.value?.sessionToken ?? null;
	}

	onPaySessionChange(paySession: PaySession) {
		this._paySessionSubject.next(paySession);
	}

	onPaySessionLeaved() {
		this._paySessionSubject.next(null);
	}

	onPaySessionUpdate(paySessionUpdate: PaySessionUpdate): PaySession | null {
		const currentPaySession = deepClone(this.paySession);
		if (!currentPaySession) {
			return null;
		}
		currentPaySession.guestCount = paySessionUpdate.guestCount;
		currentPaySession.splitType = paySessionUpdate.splitType;
		currentPaySession.checks = paySessionUpdate.checks;
		currentPaySession.status = paySessionUpdate.status;
		currentPaySession.payments = paySessionUpdate.payments;
		currentPaySession.topayAmount = paySessionUpdate.topayAmount;
		currentPaySession.serverVersion = paySessionUpdate.serverVersion;
		if (currentPaySession.status === PaySessionStatus.FINISHED) {
			console.log('PaySession finished. returning null.');
			this._paySessionSubject.next(null);
			return null;
		}
		if (this.paySession !== currentPaySession) {
			this._paySessionSubject.next(currentPaySession);
		}
		return currentPaySession;
	}

	onUnauthorized() {
		console.log('onUnauthorized()');
		this._paySessionSubject.next(null);
	}
}
